import { useCallback } from 'react';
import { MICRO_INGREDIENT_IDS } from '../utils/productSuggestions';

const getCatalogIngredient = (allIngredients, item) =>
  allIngredients?.data?.find(
    (ingredient) =>
      Number(ingredient?.id) === Number(item?.ingredient_id) ||
      ingredient?.ingredient === item?.ingredient ||
      ingredient?.ingredient_de === item?.ingredient,
  );

const getMaximumQuantity = ({ catalogIngredient, originalIngredients, item, isCustomFormula }) => {
  const configuredMaximum = parseFloat(
    isCustomFormula ? catalogIngredient?.limit_professionals : catalogIngredient?.limit_riise,
  );
  const originalIngredient = originalIngredients?.find(
    (ingredient) => String(ingredient?.ingredient_id) === String(item?.ingredient_id),
  );
  const originalQuantity = parseFloat(originalIngredient?.daily_dose);

  return Number.isFinite(originalQuantity) && originalQuantity > configuredMaximum
    ? originalQuantity
    : configuredMaximum;
};

const useFormulaEditor = ({
  setData,
  originalIngredients,
  setHasFormulaChanged,
  setError,
  setPrices,
  pricesData,
  allIngredients,
  onQuantityPriceChange,
  onFormulaInvalidated,
}) => {
  const markFormulaChanged = useCallback(() => {
    setHasFormulaChanged(true);
    onFormulaInvalidated?.();
  }, [onFormulaInvalidated, setHasFormulaChanged]);

  // id = 35 -> Bacillus coagulans Unique IS-2® (15 billion CFU), add/minus will be full 100% for this ingredient
  const onAddQuantity = useCallback(
    (item, index, isCustomFormula = false) => {
      const catalogIngredient = getCatalogIngredient(allIngredients, item);
      const maximumQuantity = getMaximumQuantity({
        catalogIngredient,
        originalIngredients,
        item,
        isCustomFormula,
      });
      const euNrv = parseFloat(catalogIngredient?.eu_nrv);

      markFormulaChanged();
      setData((currentData) => {
        const nextData = [...currentData];
        const currentQuantity = parseFloat(nextData[index].daily_dose);
        const requestedQuantity =
          Number(catalogIngredient?.id) === 35
            ? currentQuantity + maximumQuantity
            : currentQuantity + maximumQuantity * 0.1;

        if (requestedQuantity > 0 && currentQuantity !== maximumQuantity) {
          onQuantityPriceChange?.(item, requestedQuantity);
        }

        // SHOULD BE LESS THEN THE THRESHOD QUANTITY OF THE INGREDIENT
        nextData[index] = {
          ...nextData[index],
          daily_dose: requestedQuantity >= maximumQuantity ? maximumQuantity : requestedQuantity,
        };

        if (euNrv > 0 && currentQuantity !== maximumQuantity) {
          nextData[index] = {
            ...nextData[index],
            per_eu_nrv: parseInt((requestedQuantity / euNrv) * 100),
          };
        }

        return nextData;
      });
      setError((currentError) => ({ ...currentError, [item.ingredient]: false }));
    },
    [allIngredients, markFormulaChanged, onQuantityPriceChange, originalIngredients, setData, setError],
  );

  const onRemoveQuantity = useCallback(
    (item, index, isCustomFormula = false) => {
      const catalogIngredient = getCatalogIngredient(allIngredients, item);
      const maximumQuantity = getMaximumQuantity({
        catalogIngredient,
        originalIngredients,
        item,
        isCustomFormula,
      });
      const euNrv = parseFloat(catalogIngredient?.eu_nrv);

      markFormulaChanged();
      setData((currentData) => {
        const nextData = [...currentData];
        const currentQuantity = parseFloat(nextData[index].daily_dose).toFixed(5);
        const requestedQuantity =
          Number(catalogIngredient?.id) === 35
            ? currentQuantity - maximumQuantity
            : currentQuantity - maximumQuantity * 0.1;

        onQuantityPriceChange?.(item, requestedQuantity);
        nextData[index] = {
          ...nextData[index],
          daily_dose: requestedQuantity < 0 ? 0 : requestedQuantity,
        };

        if (euNrv > 0) {
          nextData[index] = {
            ...nextData[index],
            per_eu_nrv: requestedQuantity < 0 ? 0 : parseInt((requestedQuantity / euNrv) * 100),
          };
        }

        return nextData;
      });
      setError((currentError) => ({ ...currentError, [item.ingredient]: false }));
    },
    [allIngredients, markFormulaChanged, onQuantityPriceChange, originalIngredients, setData, setError],
  );

  const onInputChange = useCallback(
    (event, index, item, euNrv, ingredientData, isCustomFormula = false) => {
      let value = event.target.value;
      if (MICRO_INGREDIENT_IDS.includes(item?.ingredient_id)) value /= 1000;

      const maximumQuantity = getMaximumQuantity({
        catalogIngredient: ingredientData,
        originalIngredients,
        item,
        isCustomFormula,
      });
      const hasError = value > maximumQuantity || value < 0;

      markFormulaChanged();
      setError((currentError) => ({ ...currentError, [item.ingredient]: hasError }));
      onQuantityPriceChange?.(item, value);
      setData((currentData) => {
        const nextData = [...currentData];
        nextData[index] = {
          ...nextData[index],
          daily_dose: value,
          per_eu_nrv: parseFloat(ingredientData?.eu_nrv) <= 0 ? 0 : parseInt((value / euNrv) * 100),
        };
        return nextData;
      });
    },
    [markFormulaChanged, onQuantityPriceChange, originalIngredients, setData, setError],
  );

  const removeIngredient = useCallback(
    (ingredient) => {
      setData((currentData) =>
        currentData.filter((item) => Number(item?.ingredient_id) !== Number(ingredient?.id)),
      );
      setPrices((currentPrices) => {
        const nextPrices = { ...currentPrices };
        delete nextPrices[ingredient?.ingredient];
        return nextPrices;
      });
      markFormulaChanged();
    },
    [markFormulaChanged, setData, setPrices],
  );

  const addIngredient = useCallback(
    (ingredient) => {
      const previousIngredient = originalIngredients?.find(
        (item) => Number(item?.ingredient_id) === Number(ingredient?.id),
      );
      const ingredientData = {
        daily_dose: 0,
        description: ingredient?.description,
        ingredient: ingredient?.ingredient,
        ingredient_id: ingredient?.id,
        per_eu_nrv: 0,
        manually_added: true,
        scientific_name: ingredient?.scientific_name,
      };

      setData((currentData) => [...currentData, previousIngredient || ingredientData]);
      setPrices((currentPrices) => ({
        ...currentPrices,
        [ingredient?.ingredient]: pricesData?.[ingredient?.ingredient] || 0,
      }));
      markFormulaChanged();
    },
    [markFormulaChanged, originalIngredients, pricesData, setData, setPrices],
  );

  const removeIngredientFromBin = useCallback(
    (ingredient) => {
      setData((currentData) =>
        currentData.filter((item) => Number(item?.ingredient_id) !== Number(ingredient?.ingredient_id)),
      );
      setPrices((currentPrices) => {
        const nextPrices = { ...currentPrices };
        delete nextPrices[ingredient?.ingredient];
        return nextPrices;
      });
      markFormulaChanged();
    },
    [markFormulaChanged, setData, setPrices],
  );

  const resetFormula = useCallback(() => {
    setData(originalIngredients);
    setError({});
    setPrices(pricesData);
    setHasFormulaChanged(false);
    onFormulaInvalidated?.();
  }, [
    onFormulaInvalidated,
    originalIngredients,
    pricesData,
    setData,
    setError,
    setHasFormulaChanged,
    setPrices,
  ]);

  return {
    addIngredient,
    onAddQuantity,
    onInputChange,
    onRemoveQuantity,
    removeIngredient,
    removeIngredientFromBin,
    resetFormula,
  };
};

export default useFormulaEditor;
